import useSWR, { mutate } from 'swr';
import {getToken, isTokenExpired} from './jwtUtil';
import SiteConfig from "../siteConfig";

const domain = SiteConfig.BACKEND_DOMAIN;

// Custom fetcher function that includes the JWT token in the headers
const fetcher = async (...args) => {
  const [url, options] = args;
  const token = getToken();

  // Check if the token is expiring and the current page is not the home page
  const isExpired = isTokenExpired(token);
  if (isExpired && window.location.pathname !== "/") {
    window.location.href = "/";
    return; // Exit the function early to prevent further execution
  }
  
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      'Authorization': `Bearer ${token}`,
    },
  });
  return response.json();
};

export const getProjects = () => {
  const { data, error } = useSWR(`${domain}/api/projects`, fetcher);
  return { data, error };
};

// Custom hook to fetch account holders data for a specific user ID
export const getAccountHoldersForUserId = (userId) => {
  const { data, error } = useSWR(`${domain}/api/accountHolders`, fetcher);
  return { data, error };
};

export const getPledges = (accountHolderId) => {
  const { data, error } = useSWR(accountHolderId ? `${domain}/api/pledges?accountHolderId=${accountHolderId}`: null, fetcher);
  return { data, error };
};

export const getTransactions = (accountHolderId) => {
  const { data, error } = useSWR(accountHolderId ? `${domain}/api/transactions?accountHolderId=${accountHolderId}` : null, fetcher);
  return { data, error };
};

export const getWalletBalances = (accountHolderId) => {
  const { data, error } = useSWR(accountHolderId ? `${domain}/api/wallet/balances?accountHolderId=${accountHolderId}` : null, fetcher);
  return { data, error };
};

export const getServerWalletForAccountHolder = (accountHolderId) => {
  const { data, error } = useSWR(accountHolderId ? `${domain}/api/wallet?accountHolderId=${accountHolderId}` : null, fetcher);
  return { data, error };
};

export const createProjectBundle = async (data) => {
  const body = JSON.stringify(data);
  const url = `${domain}/api/projectBundle`;

  try {
    const token = getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body,
    });

    if (!response.ok) {
      // Handle HTTP errors
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Update the cache after the POST request
    mutate(url);

    return await response.json();
  } catch (error) {
    // Handle fetch errors
    console.error('Error creating project bundle:', error);
    throw error; // Re-throw the error after logging it
  }
};

export const createPledge = async (data) => {
  const body = JSON.stringify(data);
  const url = `${domain}/api/pledge`;

  try {
    const token = getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body,
    });

    if (!response.ok) {
      // Handle HTTP errors
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Update the cache after the POST request
    mutate(url);

    return await response.json();
  } catch (error) {
    // Handle fetch errors
    console.error('Error creating the pledge', error);
    throw error; // Re-throw the error after logging it
  }
};

export const mint = async (data) => {
  const body = JSON.stringify(data);
  const url = `${domain}/api/wallet/mint`;

  try {
    const token = getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body,
    });

    if (!response.ok) {
      // Handle HTTP errors
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Update the cache after the POST request
    mutate(url);

    return await response.json();
  } catch (error) {
    // Handle fetch errors
    console.error('Error minting', error);
    throw error; // Re-throw the error after logging it
  }
};


export const getLegacyCertificate = async (data) => {
  const url = `${domain}/api/legacyCertificate`;

  try {
    const token = getToken();
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      // Handle HTTP errors
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    // Handle fetch errors
    console.error('Error getting the Legacy Certificate', error);
    throw error; // Re-throw the error after logging it
  }
};

export const transfer = async (data) => {
  const body = JSON.stringify(data);
  const url = `${domain}/api/wallet/transfer`;

  try {
    const token = getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body,
    });

    if (!response.ok) {
      // Handle HTTP errors
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Update the wallet balance cache after the POST request
    if (data.userId) {
      const walletBalancesUrl = `${domain}/api/wallet/balances?userId=${data.userId}`;
      mutate(walletBalancesUrl);
    }

    return await response.json();
  } catch (error) {
    // Handle fetch errors
    console.error('Error performing the transfer', error);
    throw error; // Re-throw the error after logging it
  }
};

export const syncTransactions = async (data) => {
  const body = JSON.stringify(data);
  const url = `${domain}/api/transactions/sync`;

  try {
    const token = getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body,
    });

    if (!response.ok) {
      // Handle HTTP errors
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Update the cache after the POST request
    mutate(url);

    return await response.json();
  } catch (error) {
    // Handle fetch errors
    console.error('Error syncing the transactions', error);
    throw error; // Re-throw the error after logging it
  }
};